/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useState } from 'react';

import { Typography2 } from '@coursera/cds-core';

import { useFreeTrialCTACopyExperimentData } from 'bundles/coursera-plus-common/components/elevateLTV/startFreeTrial/useGenerateStartFreeTrialTestEpicImpression';
import useArticleUrlData from 'bundles/shared-articles/hooks/useArticleUrlData';
import VLPVideoPlayer from 'bundles/unified-seo-content-common/components/video-logged-out-page-video-player/VLPVideoPlayer';
import VLPVideoPlayerPlaceholder from 'bundles/unified-seo-content-common/components/video-logged-out-page-video-player/VLPVideoPlayerPlaceholder';
import useVLPVideoMetadata from 'bundles/unified-seo-content-common/hooks/useVLPVideoMetadata';
import CourseVideoOverview from 'bundles/video-logged-out-page/components/CourseVideoOverview';
import type { VideoPlayer } from 'bundles/video-logged-out-page/types/vlpSharedTypes';

type InputProps = {
  courseSlug: string;
  itemId: string;
  startAt?: number | string | null;
  videoDescription?: string | null;
};

type Props = InputProps;

const VLPVideoPlayerContainer = ({ courseSlug, videoDescription, itemId, startAt }: Props) => {
  const [videoPlayer, setVideoPlayer] = useState<VideoPlayer | null>(null);
  const [showVideoOverlay, setShowVideoOverlay] = useState(false);
  const [videoIsReady, setVideoIsReady] = useState(false);
  const [showPlaceholder, setShowPlaceholder] = useState(true);
  const { slug: articleSlug } = useArticleUrlData();
  const { onDemandVideos, cdpMetadata, tracks, reviewOverview, loading } = useVLPVideoMetadata({
    courseSlug,
    itemId,
  });
  const { isSessionBasedFreeTrialCopyEnabled, isUserBasedFreeTrialCopyEnabled } = useFreeTrialCTACopyExperimentData({
    courseId: cdpMetadata?.id,
  });
  const shouldShowFreeTrialCtaCopy = isSessionBasedFreeTrialCopyEnabled || isUserBasedFreeTrialCopyEnabled;

  const onPlayVideo = () => {
    setShowVideoOverlay(false);
    setShowPlaceholder(false);
  };

  useEffect(() => {
    const shouldAutoPlayVideo = !showVideoOverlay && !showPlaceholder && videoIsReady && videoPlayer;
    if (shouldAutoPlayVideo) {
      videoPlayer?.play();
    }
  }, [showVideoOverlay, showPlaceholder, videoIsReady, videoPlayer]);

  useEffect(() => {
    const setVideoTime = (time: string | number) => {
      if (videoPlayer) {
        videoPlayer.currentTime(time);
      }
    };

    if (showVideoOverlay && videoIsReady) {
      onPlayVideo();
    }
    if (startAt) {
      setVideoTime(startAt);
    }
    return () => {
      if (videoPlayer) {
        videoPlayer.off('timeupdate');
        videoPlayer.off('ended');
      }
    };
  }, [showVideoOverlay, videoIsReady, videoPlayer, startAt]);

  if (loading) {
    return <VLPVideoPlayerPlaceholder />;
  }

  if (!cdpMetadata || !onDemandVideos) {
    return null;
  }

  const { id: courseId, name: courseName, partners = [], primaryS12n, ratings, totalEnrollmentCount } = cdpMetadata;

  const getVideoPlayerObject = (vjsPlayer: VideoPlayer) => {
    vjsPlayer.ready(() => {
      setVideoPlayer(vjsPlayer);
      setVideoIsReady(true);
      setShowVideoOverlay(true);
    });
  };

  return (
    <div
      className="rc-ArticleVLPVideoPlayerContainer"
      css={css`
        margin: 48px 0;
      `}
      data-e2e="article-vlp-video-player"
    >
      <VLPVideoPlayer
        courseSlug={courseSlug}
        articleSlug={articleSlug}
        itemId={itemId}
        showVideoOverlay={showVideoOverlay}
        videoIsReady={videoIsReady}
        getVideoPlayerObject={getVideoPlayerObject}
        onPlayVideo={onPlayVideo}
        onDemandVideos={onDemandVideos}
        videoPlayer={videoPlayer}
        tracks={tracks}
        showPlaceholder={showPlaceholder}
      />
      <Typography2
        variant="bodySecondary"
        component="div"
        color="supportText"
        css={css`
          margin-bottom: 24px;
        `}
      >
        {videoDescription}
      </Typography2>
      <CourseVideoOverview
        courseId={courseId}
        courseName={courseName}
        courseSlug={courseSlug}
        ctaLabel={shouldShowFreeTrialCtaCopy ? 'Start free trial' : undefined}
        partners={partners}
        ratings={ratings}
        totalReviewCount={reviewOverview?.totalReviewCount || ratings?.ratingCount}
        totalEnrollmentCount={totalEnrollmentCount}
        primaryS12n={primaryS12n}
      />
    </div>
  );
};

export default VLPVideoPlayerContainer;
